import { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';

interface MobileHeaderAction {
  action: ReactNode;
  position: 'left' | 'right';
}

interface MobileHeaderActionsContextValue {
  actions: MobileHeaderAction[];
  addMobileHeaderAction: (action: ReactNode, position: 'left' | 'right') => symbol;
  removeMobileHeaderAction: (id: symbol) => void;
}

// Default context placeholder
const MobileHeaderActionsContext = createContext<MobileHeaderActionsContextValue>({
  actions: [],
  addMobileHeaderAction: () => Symbol(),
  removeMobileHeaderAction: (_id: symbol) => {}
});

export const MobileHeaderActionsProvider = ({ children }: { children: ReactNode }) => {
  const [actionMap, setActionMap] = useState<Map<symbol, MobileHeaderAction>>(new Map());

  const addMobileHeaderAction = useCallback((action: ReactNode, position: 'left' | 'right'): symbol => {
    const id = Symbol();
    setActionMap((prev) => {
      const next = new Map(prev);
      next.set(id, { action, position });
      return next;
    });
    return id;
  }, []);

  const removeMobileHeaderAction = useCallback((id: symbol) => {
    setActionMap((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const actions = useMemo(() => Array.from(actionMap.values()), [actionMap]);

  const value = useMemo(
    () => ({ actions, addMobileHeaderAction, removeMobileHeaderAction }),
    [actions, addMobileHeaderAction, removeMobileHeaderAction]
  );

  return <MobileHeaderActionsContext.Provider value={value}>{children}</MobileHeaderActionsContext.Provider>;
};

export const useMobileHeaderActions = () => useContext(MobileHeaderActionsContext);
